<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_TYPES, MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  isGroupConversation,
  sender,
  messageType,
  contentAttributes,
} = useMessageContext();
const { t } = useI18n();
const { isAWhatsAppChannel } = useInbox();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-[#3cbeb6] text-white',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-[#222375] text-white',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-[#00aeef] text-white text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

// WhatsApp delivery ticks matter per message, so grouped outgoing bubbles keep their meta.
const keepMetaWhenGrouped = computed(
  () => isAWhatsAppChannel.value && messageType.value === MESSAGE_TYPES.OUTGOING
);

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    (!shouldGroupWithNext.value || keepMetaWhenGrouped.value) &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const metaMutedClass = computed(() => {
  if (
    variant.value === MESSAGE_VARIANTS.USER ||
    variant.value === MESSAGE_VARIANTS.AGENT
  ) {
    return 'text-white/70';
  }
  return 'text-n-slate-11';
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

const replyPreviewProseClass = computed(() => {
  if (
    variant.value === MESSAGE_VARIANTS.USER ||
    variant.value === MESSAGE_VARIANTS.AGENT
  ) {
    return 'prose-bubble-light';
  }
  return 'prose-bubble';
});

const shouldShowSenderName = computed(() => {
  return (
    isGroupConversation?.value &&
    orientation.value === ORIENTATION.LEFT &&
    sender?.value?.name &&
    variant.value === MESSAGE_VARIANTS.USER
  );
});

const senderName = computed(() => sender?.value?.name || '');
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="shouldShowSenderName"
      class="mb-1 text-xs font-semibold text-white/80"
    >
      {{ senderName }}
    </div>
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg transition-colors cursor-pointer bg-n-alpha-black1 hover:bg-n-alpha-black2"
      :title="t('CONVERSATION.GO_TO_REPLIED_MESSAGE')"
      @click="scrollToMessage"
    >
      <div
        v-if="contentAttributes?.isReaction"
        class="mb-1 text-xs font-medium opacity-70"
      >
        {{ t('CONVERSATION.REACTED_TO') }}
      </div>
      <div
        v-dompurify-html="replyToPreview"
        class="prose line-clamp-2"
        :class="replyPreviewProseClass"
      />
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : metaMutedClass,
      ]"
      class="mt-2"
    />
  </div>
</template>
